import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ExportPaymentPDF } from "./ExportPaymentPDF";
import { formatDate } from "./../ui/formatDate";
import { ColorRing } from "react-loader-spinner";

function PaymentHistoryTable({
  clientId,
  fetchPaymentSummary,
  clientName,
  paymentSummary,
  paymentSummaryCard,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    if (!clientId) return;
    fetchPaymentSummary();
  }, [clientId, fetchPaymentSummary]);

  const handlePaymentHistoryClick = () => {
    navigate(`/admin/order/client/clientDetails/${clientId}`);
  };

  return (
    <div className="bg-white">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
        <h2 className="text-xl font-semibold text-gray-800">Payment History</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <ExportPaymentPDF
            paymentSummaryCard={paymentSummaryCard}
            paymentData={paymentSummary}
            clientName={clientName}
          />
          {userRole === "sales executive" || userRole === "admin" ? (
            <button
              onClick={handlePaymentHistoryClick}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium rounded-md shadow-lg hover:bg-indigo-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                  clipRule="evenodd"
                />
              </svg>
              Order Details
            </button>
          ) : null}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center">
          <ColorRing
            visible={true}
            height="80"
            width="80"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
          />
        </div>
      ) : (
        <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
          <table className="min-w-full table-auto rounded-lg overflow-scroll border-spacing-0 border-collapse">
            <thead className="bg-gray-700 text-white sticky top-0 z-10">
              <tr>
                <th className="p-2 text-left font-normal tracking-wide">#</th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Time Stamp
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Date
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Transaction Id
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Sold Amount
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Received Amount
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Transfer In
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Transfer Out
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Transfer By
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Transfer To
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Current Balance
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Remarks
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Narrations
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Payment Type
                </th>
                <th className="p-2 text-left font-normal tracking-wide">
                  Payment Mode
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {paymentSummary.map((entry, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                >
                  <td className="px-2 py-0 5">{index + 1}</td>
                  <td className="px-2 py-0.5">
                    {entry.createdAt ? formatDate(entry.createdAt) : "-"}
                  </td>
                  <td className="px-2 py-0.5">{entry.date}</td>
                  <td className="px-2 py-0.5 font-medium text-red-600 uppercase">
                    {entry.transactionId}
                  </td>
                  <td className="px-2 py-0.5">
                    {entry.soldAmount === 0 ? "-" : entry.soldAmount.toFixed(2)}
                  </td>
                  <td className="px-2 py-0.5">
                    {entry.receivedAmount === 0
                      ? "-"
                      : entry.receivedAmount.toFixed(2)}
                  </td>
                  <td className="px-2 py-0.5">
                    {entry.transferIn === 0 ? "-" : entry.transferIn.toFixed(2)}
                  </td>
                  <td className="px-2 py-0.5">
                    {entry.transferOut === 0
                      ? "-"
                      : entry.transferOut.toFixed(2)}
                  </td>
                  <td className="px-2 py-0.5 font-medium text-yellow-700">
                    {entry.sentBy || "-"}
                  </td>
                  <td className="px-2 py-0.5 font-medium text-blue-600">
                    {entry.sentTo || "-"}
                  </td>
                  <td className="px-2 py-0.5">
                    {entry.currentBalance === 0 ? (
                      ""
                    ) : entry.currentBalance < 0 ? (
                      <div className="text-green-700 font-medium">
                        {Math.abs(entry.currentBalance).toFixed(2)}{" "}
                        <span className="text-xs">Adv.</span>
                      </div>
                    ) : (
                      entry.currentBalance.toFixed(2)
                    )}
                  </td>
                  <td className="px-2 py-0.5 font-medium text-red-600">
                    {entry.remarks === 0 ? "-" : entry.remarks}
                  </td>
                  <td className="px-2 py-0.5 font-medium text-red-600">
                    {entry.narration === 0 ? "-" : entry.narration}
                  </td>
                  <td className="px-2 py-0.5">{entry.orderPaymentType}</td>
                  <td className="px-2 py-0.5">{entry.paymentMode || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PaymentHistoryTable;
